import {Logger} from '../utils/Logger';
import {User} from '../model/user.model';
import {Validator} from '../utils/Validator';
import {Step} from '../application-flow/steps/step';
import {FormGroupUtil} from '../utils/FormGroupUtil';
import {Component, OnInit, Type} from '@angular/core';
import {UserAddress} from '../model/user-address.model';
import {AnalyticsTracker} from '../utils/AnalyticsTracker';
import {CityInteractor} from '../interactors/city.interactor';
import {UserInteractor} from '../interactors/user.interactor';
import {StateInteractor} from '../interactors/state.interactor';
import {LoanApplication} from '../model/loan-application.model';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {UserAddressInteractor} from '../interactors/user-address.interactor';
import {LoanApplicationInteractor} from '../interactors/loan-application.interactor';
import {PersonalInformationStep} from '../application-flow/steps/personal-information.step';
import {LoanApplicationFlowNavigator} from '../application-flow/loan-application-flow.navigator';
import {AbstractApplicationFlowComponent} from '../application-flow/abstract-application-flow.component';
import {LenderDetailsService} from '../service/lender-details.service';
import {isEmpty} from 'lodash';
import {CreditAssessment} from '../model/eligibility-check/credit-assessment.model';
import {ActivatedRoute} from '@angular/router';
import {executeWithLoader, LoaderStatus} from '../utils/loader.util';
import * as moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
    templateUrl: 'personal-info.component.html',
    styleUrls: ['../../assets/styles/finzy-styles.scss', './personal-info.component.css', '../loan-application-flow-component/application-flow.css']
})
export class PersonalInfoComponent extends AbstractApplicationFlowComponent implements OnInit {
    private static readonly DateFormat = 'DD/MM/YYYY';
    private static readonly MaximumDateOfBirth = moment().subtract(18, 'years').toDate();
    private static readonly MinimumDateOfBirth = moment().subtract(150, 'years').toDate();
    public readonly GenderOptions = [
        {displayName: 'Male', value: 'Male'},
        {displayName: 'Female', value: 'Female'},
        {displayName: 'Others', value: 'Others'},
    ];
    public readonly MaritalStatusOptions = [
        {value: 'Single', displayName: 'Single'},
        {value: 'Married', displayName: 'Married'},
        {value: 'Divorced', displayName: 'Divorced'},
        {value: 'Separated', displayName: 'Separated'}
    ];
    public readonly QualificationOptions = [
        {value: 'Secondary', displayName: 'Secondary'},
        {value: 'Higher Secondary', displayName: 'Higher Secondary'},
        {value: 'Graduate', displayName: 'Graduate'},
        {value: 'Post Graduate', displayName: 'Post Graduate'}
    ];
    public readonly cities = [];
    public readonly states = [];
    public filteredCities: Observable<string[]>;
    public readonly formGroup: FormGroup;
    public loaderStatus = new LoaderStatus();
    public user: User;
    public userAddress: UserAddress;
    public application: LoanApplication;
    private readonly userInteractor: UserInteractor;
    private readonly cityInteractor: CityInteractor;
    private readonly stateInteractor: StateInteractor;
    private readonly analyticsTracker: AnalyticsTracker;
    private readonly userAddressInteractor: UserAddressInteractor;
    private readonly applicationInteractor: LoanApplicationInteractor;

    constructor(cityInteractor: CityInteractor, stateInteractor: StateInteractor, loanApplicationFlowNavigator: LoanApplicationFlowNavigator,
                formBuilder: FormBuilder, userInteractor: UserInteractor, userAddressInteractor: UserAddressInteractor,
                applicationInteractor: LoanApplicationInteractor, analyticsTracker: AnalyticsTracker, private lenderDetailsService: LenderDetailsService,
                private activatedRoute: ActivatedRoute) {
        super(loanApplicationFlowNavigator);
        this.cityInteractor = cityInteractor;
        this.stateInteractor = stateInteractor;
        this.userInteractor = userInteractor;
        this.analyticsTracker = analyticsTracker;
        this.userAddressInteractor = userAddressInteractor;
        this.applicationInteractor = applicationInteractor;
        this.formGroup = formBuilder.group({
            gender: formBuilder.control(null, [Validators.required]),
            maritalStatus: formBuilder.control(null, [Validators.required]),
            dateOfBirth: formBuilder.control(null, [Validators.required,
                Validator.maxDate(PersonalInfoComponent.MaximumDateOfBirth),
                Validator.minDate(PersonalInfoComponent.MinimumDateOfBirth)]),
            educationalQualification: formBuilder.control(null, [Validators.required]),
            addressStreet: formBuilder.control(null, [Validators.required, Validators.maxLength(100)]),
            addressCity: formBuilder.control(null, [Validators.required]),
            addressState: formBuilder.control(null, [Validators.required]),
            addressPincode: formBuilder.control(null, [Validators.required, Validators.pattern(/\d{6}/), Validators.maxLength(6)])
        });

        this.activatedRoute.data.subscribe(data => {
            if (data.creditAssessment) {
                const creditAssessment: CreditAssessment = data.creditAssessment;

                this.formGroup.patchValue({
                    dateOfBirth: creditAssessment.userDetails.dob,
                    addressState: creditAssessment.userDetails.state,
                    addressPincode: creditAssessment.userDetails.postal
                });
            }
        });
    }

    maximumDateOfBirthAllowed() {
        return PersonalInfoComponent.MaximumDateOfBirth;
    }

    minimumDateOfBirthAllowed() {
        return PersonalInfoComponent.MinimumDateOfBirth;
    }

    async ngOnInit(): Promise<void> {
        this.formGroup.disable();
        (await this.cityInteractor.getAll()).forEach(city => this.cities.push(city.name));
        (await this.stateInteractor.getAll()).forEach(state => this.states.push(state.name));
        this.cities.sort();
        this.states.sort();
        this.user = await this.userInteractor.get();
        this.application = await this.applicationInteractor.get();
        this.userAddress = await this.getUserAddress();
        await this.onPrefillInformationReceived(this.formGroup, this.user, this.userAddress);
        this.formGroup.enable();
    }

    async onPrefillInformationReceived(formGroup: FormGroup, user: User, userAddress: UserAddress): Promise<void> {
        const dateOfBirth = user.dob ? moment(user.dob).toDate() : (formGroup.controls.dateOfBirth.value ? moment(formGroup.controls.dateOfBirth.value).toDate() : undefined);

        formGroup.patchValue({
            gender: user.gender,
            maritalStatus: user.marital_status,
            dateOfBirth: dateOfBirth,
            educationalQualification: user.educational_qualification,
            addressStreet: userAddress ? userAddress.address : undefined,
            addressCity: userAddress ? userAddress.city : undefined,
            addressState: userAddress ? userAddress.state : formGroup.controls.addressState.value,
            addressPincode: userAddress ? userAddress.pin_code : formGroup.controls.addressPincode.value
        });
    }
    
    async onSubmit(formGroup: FormGroup) {
        await executeWithLoader(this.loaderStatus, async () => {
            FormGroupUtil.validateFormGroup(formGroup);
            if (formGroup.invalid || this.isFormObjectIsHavingAllNullValues(formGroup)) {
                Logger.log(`Form is not valid. Not going ahead with the submission.`);
                return;
            }
            const user = this.user;
            user.gender = formGroup.controls.gender.value;
            user.marital_status = formGroup.controls.maritalStatus.value;
            user.dob = moment.tz(formGroup.controls.dateOfBirth.value, PersonalInfoComponent.DateFormat, 'Asia/Kolkata').toISOString();
            user.educational_qualification = formGroup.controls.educationalQualification.value;
            const userAddress = <UserAddress>{
                address: formGroup.controls.addressStreet.value,
                city: formGroup.controls.addressCity.value,
                state: formGroup.controls.addressState.value,
                pin_code: formGroup.controls.addressPincode.value,
                _id: this.userAddress ? this.userAddress._id : undefined,
                updatedAt: this.userAddress ? this.userAddress.updatedAt : undefined
            };
            await this.userInteractor.update(user);
            const addressId = await this.userAddressInteractor.create(userAddress, user._id);

            const application = this.application;
            application.addressId = addressId;
            await this.applicationInteractor.update(application);
            await this.applicationInteractor.saveApplicationToStorage(application);
            this.analyticsTracker.trackButtonClick('Loan Information', 'Next');
            await this.nextPage();
        });
    }

    step(): Type<Step> {
        return PersonalInformationStep;
    }

    getCityName() {
        let cityName = this.formGroup.controls.addressCity.value;
        if (cityName?.length < 3) {
            this.filteredCities = null;
        } else {
            this.filteredCities =  this.formGroup.controls.addressCity.valueChanges
            .pipe(startWith(cityName), map((value :string) => this.filterCityNames(value)));
        }
    }

    private filterCityNames(val: string): string[] {
        return this.cities.filter(city => city.toLowerCase().startsWith(val?.toLowerCase()));
    }

    private isFormObjectIsHavingAllNullValues(formGroup: FormGroup): boolean {
        if(formGroup.value) {
            return !Object.values(formGroup.value).some((val) => val);
        }
    }

    validateCity(event: Event) {
        const cityName = this.formGroup.controls.addressCity.value;
        const isCityPresent = !!this.cities.find((city) => city == cityName);
        if (!isCityPresent) {
            this.formGroup.controls.addressCity.setErrors({'cityListError': true});
        }
    }

    protected async getUserAddress(): Promise<UserAddress> {
        try {
            if (!isEmpty(this.application.addressId)) {
                return await this.userAddressInteractor.get(this.application.addressId);
            }
            const lenderDetails = await this.lenderDetailsService.getLenderInfo(this.user._id);
            lenderDetails.correspondence_address._id = undefined;
            lenderDetails.correspondence_address.proofs = undefined;
            return lenderDetails.correspondence_address;
        } catch (e) {
            return undefined;
        }
    }

    protected errorMessages(): object {
        return {
            gender: [{
                type: 'required',
                message: 'Please select your gender'
            }],
            dateOfBirth: [{
                type: 'required',
                message: 'Please provide your date of birth'
            }, {
                type: 'min',
                message: `Please provide a date after ${moment(PersonalInfoComponent.MinimumDateOfBirth).format('DD MMM YYYY')}`
            }, {
                type: 'max',
                message: `Please provide a date before ${moment(PersonalInfoComponent.MaximumDateOfBirth).format('DD MMM YYYY')}`
            }],
            maritalStatus: [{
                type: 'required',
                message: 'Please select your marital status'
            }],
            educationalQualification: [{
                type: 'required',
                message: 'Please select your educational qualification'
            }],
            addressStreet: [{
                type: 'required',
                message: 'Please provide your address'
            }, {
                type: 'maxlength',
                message: 'maximum 100 characters'
            }],
            addressCity: [{
                type: 'required',
                message: 'Please select your city'
            }, {
                type: 'cityListError',
                message: 'Please select a valid option'
            }],
            addressState: [{
                type: 'required',
                message: 'Please select your state'
            }],
            addressPincode: [{
                type: 'required',
                message: 'Please provide your pincode'
            }, {
                type: 'pattern',
                message: 'Please a valid pincode'
            }, {
                type: 'maxlength',
                message: 'Please enter a pincode of 6 characters'
            }]
        };
    }
}
